import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, Send, ShieldCheck, AlertTriangle, Siren, QrCode, Sparkles, BellRing, RefreshCw } from 'lucide-react';
import { useLang } from '../i18n';
import { Card, Button, Badge, Input, Spinner, Reveal } from '../components/ui';
import api, { vnd } from '../api';

export default function Carry() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState('');
  const [chat, setChat] = useState([]);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState('');

  const refresh = (keepId) =>
    api.get('/policies').then((r) => {
      setPolicies(r.data);
      const sel = keepId ? r.data.find((p) => p.id === keepId) : r.data[0];
      if (sel) setActive(sel);
    }).catch(() => {});

  useEffect(() => { refresh(); }, []);

  const loadExpiring = async () => {
    setBusy('expiring');
    try { const r = await api.post('/demo/expiring-policy'); await refresh(r.data.id); }
    finally { setBusy(''); }
  };

  const renew = async () => {
    if (!active) return;
    setBusy('renew');
    try { await api.post(`/policies/${active.id}/renew`); await refresh(active.id); }
    finally { setBusy(''); }
  };

  const ask = async (question) => {
    const qq = question || q;
    if (!qq || !active) return;
    setChat((c) => [...c, { role: 'user', text: qq }]);
    setQ(''); setAsking(true);
    try {
      const r = await api.post('/policies/ask', { policy_id: active.id, question: qq, language: lang });
      setChat((c) => [...c, { role: 'ai', text: r.data.answer, grounded: r.data.grounded }]);
    } catch (e) {
      setChat((c) => [...c, { role: 'ai', text: 'Error answering.', grounded: false }]);
    }
    setAsking(false);
  };

  const name = (p) => (lang === 'vi' ? p.name_vi : p.name_en);

  return (
    <div className="max-w-5xl mx-auto px-5 py-12" data-testid="carry-page">
      <Reveal>
        <div className="kicker">{t('moment_carry')}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-2">{t('carry_h')}</h1>
        <p className="text-slate-400 mt-2">{t('carry_sub')}</p>
      </Reveal>

      {policies.length === 0 ? (
        <Card className="p-8 mt-8 text-center" data-testid="carry-empty">
          <Wallet className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">{t('no_policies')}</p>
          <div className="flex flex-wrap gap-2 justify-center mt-5">
            <Button onClick={() => nav('/buy')}>{t('nav_buy')}</Button>
            <Button variant="ghost" data-testid="carry-empty-load-expiring" onClick={loadExpiring} disabled={busy === 'expiring'}>
              {busy === 'expiring' ? <Spinner label="…" /> : <><BellRing className="w-4 h-4" /> {t('load_expiring')}</>}
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-5 gap-5 mt-8">
          {/* policy list */}
          <div className="lg:col-span-2 space-y-3">
            <div className="flex items-center justify-between mb-1">
              <div className="kicker">{t('your_policies')}</div>
              <button data-testid="load-expiring-demo" onClick={loadExpiring} disabled={busy === 'expiring'} className="chip px-2.5 py-1 text-[10px] font-mono text-slate-400 flex items-center gap-1">
                <BellRing className="w-3 h-3" /> {t('load_expiring')}
              </button>
            </div>
            {policies.map((p) => (
              <button key={p.id} data-testid={`policy-item-${p.policy_no}`} onClick={() => { setActive(p); setChat([]); }}
                className={`card w-full text-left p-4 transition-colors ${active?.id === p.id ? 'border-jade/50' : ''}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm">{p.policy_no}</span>
                  <Badge tone={p.status === 'active' ? 'jade' : 'slate'}>{p.status}</Badge>
                </div>
                <div className="text-sm mt-2">{p.vehicle.make} {p.vehicle.model} {p.vehicle.year}</div>
                <div className="text-xs text-slate-500 mt-1">{vnd(p.premium_vnd)} {t('per_year')}</div>
                {p.renewal?.due && <div className="text-[10px] font-mono text-amber mt-1.5 flex items-center gap-1"><BellRing className="w-3 h-3" /> {t('renewal_due')}</div>}
              </button>
            ))}
          </div>

          {/* active policy + assistant */}
          {active && (
            <div className="lg:col-span-3 space-y-5">
              <Card className="p-6 relative overflow-hidden" data-testid="policy-wallet-card">
                <div className="absolute -top-8 -right-8 w-32 h-32 bg-jade/10 blur-3xl rounded-full" />
                {active.renewal?.due && (
                  <div className="mb-4 rounded-xl border border-amber/40 bg-amber/10 p-3" data-testid="renewal-nudge">
                    <div className="flex items-center gap-2 text-amber text-sm font-semibold"><BellRing className="w-4 h-4" /> {t('renewal_due')}{active.renewal.days_left >= 0 ? ` · ${active.renewal.days_left} ${t('days')}` : ''}</div>
                    <div className="text-xs text-slate-300 mt-1.5 leading-relaxed">{active.renewal.message}</div>
                    <Button data-testid="renew-button" onClick={renew} disabled={busy === 'renew'} className="mt-3 !py-2 text-xs">
                      {busy === 'renew' ? <Spinner label={t('renewing')} /> : <><RefreshCw className="w-3.5 h-3.5" /> {t('renew_now')} · {vnd(active.renewal.renew_premium_vnd)}</>}
                    </Button>
                  </div>
                )}
                <div className="flex items-start justify-between">
                  <div>
                    <div className="kicker">{t('brand')} · {t('policy_no')}</div>
                    <div className="font-mono text-xl mt-1">{active.policy_no}</div>
                  </div>
                  <div className="w-14 h-14 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                    <QrCode className="w-8 h-8 text-jade" />
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                  <div><div className="text-xs text-slate-500">Vehicle</div>{active.vehicle.make} {active.vehicle.model}</div>
                  <div><div className="text-xs text-slate-500">{t('valid_until')}</div>{active.end_date.slice(0, 10)}</div>
                </div>
                <div className="mt-4">
                  <div className="text-xs text-slate-500 mb-1.5">{t('coverage_label')}</div>
                  <div className="flex flex-wrap gap-2">
                    {active.products.map((p) => <Badge key={p.code} tone="slate">{name(p)}</Badge>)}
                  </div>
                </div>
                <Button variant="ghost" data-testid="carry-start-claim" onClick={() => nav('/claim', { state: { policyId: active.id } })} className="mt-5 w-full">
                  <Siren className="w-4 h-4" /> {t('start_claim_btn')}
                </Button>
              </Card>

              {/* AI assistant */}
              <Card className="p-5" data-testid="policy-assistant">
                <div className="flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-jade" /><span className="kicker">{t('ask_ai')}</span></div>

                <div className="mb-3">
                  <div className="text-xs text-slate-500 mb-2">{t('suggested_q')}</div>
                  <div className="flex flex-wrap gap-2">
                    {['q1', 'q2', 'q3'].map((k) => (
                      <button key={k} data-testid={`suggested-${k}`} onClick={() => ask(t(k))} className="chip px-3 py-1.5 text-xs text-slate-300">{t(k)}</button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3 max-h-72 overflow-y-auto mb-3">
                  {chat.map((m, i) => (
                    <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.role === 'user' ? 'bg-jade text-ink' : 'bg-white/5 border border-white/10 text-slate-200'}`}>
                        <div className="whitespace-pre-line">{m.text}</div>
                        {m.role === 'ai' && (
                          <div className={`mt-2 text-[10px] font-mono uppercase flex items-center gap-1 ${m.grounded ? 'text-jade' : 'text-amber'}`}>
                            {m.grounded ? <ShieldCheck className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                            {m.grounded ? t('grounded') : t('not_grounded')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {asking && <Spinner label="…" />}
                </div>

                <div className="flex gap-2">
                  <Input data-testid="policy-question-input" value={q} onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && ask()} placeholder={t('ask_ph')} />
                  <Button data-testid="policy-ask-button" onClick={() => ask()} disabled={asking || !q}><Send className="w-4 h-4" /></Button>
                </div>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
